/**
 * ============================================================================
 * Deliadore Care System - Premium After-Sales Support & Maintenance JS Engine
 * Architecture: ES6 Class Modular Architecture (Shopify Forms Headless Bridge)
 * Author: Senior Shopify Developer & Frontend Architect
 * Namespace: dc- (100% Isolated, Zero Global Pollutions)
 * ============================================================================
 */

(function () {
  'use strict';

  class DeliadoreCare {
    constructor(rootElement) {
      this.root = rootElement;
      this.sectionId = this.root.dataset.sectionId || 'default';
      
      this.state = {
        currentView: 'home',
        currentStep: 1,
        totalSteps: 6,
        uploadedPhotos: [],
        uploadedVideo: null,
        maxPhotos: 6,
        maxPhotoSize: 5 * 1024 * 1024,
        maxVideoSize: 25 * 1024 * 1024,
        generatedTicket: null
      };

      this.cacheElements();
      this.init();
      this.forceHideShopifyForm();
    }

    /**
     * Forces external Shopify Form element to stay hidden while acting as a background motor
     */
    forceHideShopifyForm() {
      const hideElements = () => {
        const selectors = [
          'shopify-form', 
          '[data-shopify-forms-container]', 
          '.shopify-block:has([data-form-id="1082123"])', 
          'div[id*="1082123"]',
          'div:has(> iframe[src*="forms"])'
        ];
        
        selectors.forEach(sel => {
          document.querySelectorAll(sel).forEach(el => {
            el.style.setProperty('display', 'none', 'important');
            el.style.setProperty('opacity', '0', 'important');
            el.style.setProperty('visibility', 'hidden', 'important');
            el.style.setProperty('height', '0', 'important');
            el.style.setProperty('overflow', 'hidden', 'important');
          });
        });
      };

      hideElements();
      window.addEventListener('load', hideElements);
      setTimeout(hideElements, 500);
    }

    cacheElements() {
      this.views = {
        home: this.root.querySelector('#dc-view-home'),
        wizard: this.root.querySelector('#dc-view-wizard'),
        success: this.root.querySelector('#dc-view-success'),
        tracking: this.root.querySelector('#dc-view-tracking'),
        policy: this.root.querySelector('#dc-view-policy'),
        faq: this.root.querySelector('#dc-view-faq')
      };

      this.navButtons = this.root.querySelectorAll('[data-action="navigate"]');
      this.btnPrev = this.root.querySelector('#dc-btn-prev');
      this.btnNext = this.root.querySelector('#dc-btn-next');
      this.btnSubmit = this.root.querySelector('#dc-btn-submit');

      this.wizardForm = this.root.querySelector('#dc-wizard-form');
      this.wizardSteps = this.root.querySelectorAll('.dc-wizard-step');
      this.stepIndicators = this.root.querySelectorAll('[data-step-indicator]');
      this.progressFill = this.root.querySelector('#dc-progress-fill');

      this.photoDropzone = this.root.querySelector('#dc-photo-dropzone');
      this.photoInput = this.root.querySelector('#dc-photo-input');
      this.photoPreviewGrid = this.root.querySelector('#dc-photo-preview-grid');
      
      this.videoDropzone = this.root.querySelector('#dc-video-dropzone');
      this.videoInput = this.root.querySelector('#dc-video-input');
      this.videoPreviewGrid = this.root.querySelector('#dc-video-preview-grid');

      this.descInput = this.root.querySelector('#dc-description');
      this.charCountDisplay = this.root.querySelector('#dc-char-count');

      this.trackingForm = this.root.querySelector('#dc-tracking-form');
      this.timelineContainer = this.root.querySelector('#dc-timeline-container');
      this.timelineItems = this.root.querySelectorAll('.dc-timeline-item');

      this.accordionTriggers = this.root.querySelectorAll('.dc-accordion-trigger');
      this.toastContainer = this.root.querySelector('#dc-toast-container');
      this.skeletonOverlay = this.root.querySelector('#dc-skeleton-overlay');
    }

    init() {
      this.bindNavigationEvents();
      this.bindWizardEvents();
      this.bindMediaUploadEvents();
      this.bindFormValidationEvents();
      this.bindAccordionEvents();
      this.bindTrackingEvents();
      this.initIntersectionObserver();
      this.initSmoothScroll();
    }

    bindNavigationEvents() {
      this.navButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const targetView = btn.dataset.target;
          
          if (btn.dataset.fillTicket === 'true' && this.state.generatedTicket) {
            const trackInput = this.root.querySelector('#dc-track-ticket');
            if (trackInput) trackInput.value = this.state.generatedTicket;
          }

          this.switchView(targetView);
        });
      });
    }

    switchView(targetViewName) {
      const targetViewElement = this.views[targetViewName];
      if (!targetViewElement) return;

      Object.values(this.views).forEach((view) => {
        if (!view.classList.contains('dc-hidden')) {
          view.classList.remove('dc-view--active');
          setTimeout(() => {
            view.classList.add('dc-hidden');
          }, 300);
        }
      });

      this.scrollToTop();

      setTimeout(() => {
        targetViewElement.classList.remove('dc-hidden');
        void targetViewElement.offsetWidth;
        targetViewElement.classList.add('dc-view--active');
        this.state.currentView = targetViewName;
      }, 310);
    }

    scrollToTop() {
      const offset = this.root.getBoundingClientRect().top + window.pageYOffset - 40;
      window.scrollTo({ top: offset, behavior: 'smooth' });
    }

    bindWizardEvents() {
      if (this.btnNext) {
        this.btnNext.addEventListener('click', () => {
          if (this.validateCurrentStep()) {
            this.goToStep(this.state.currentStep + 1);
          } else {
            this.showToast('Lütfen zorunlu alanları eksiksiz ve kurallara uygun doldurunuz.', 'error');
          }
        });
      }

      if (this.btnPrev) {
        this.btnPrev.addEventListener('click', () => {
          if (this.state.currentStep > 1) {
            this.goToStep(this.state.currentStep - 1);
          }
        });
      }

      if (this.wizardForm) {
        this.wizardForm.addEventListener('submit', (e) => {
          e.preventDefault();
          if (this.validateCurrentStep()) {
            this.handleHeadlessSubmit();
          }
        });
      }
    }

    goToStep(targetStep) {
      if (targetStep < 1 || targetStep > this.state.totalSteps) return;

      const currentStepEl = this.root.querySelector(`.dc-wizard-step[data-step="${this.state.currentStep}"]`);
      if (currentStepEl) {
        currentStepEl.classList.add('dc-hidden');
        currentStepEl.classList.remove('dc-wizard-step--active');
      }

      const targetStepEl = this.root.querySelector(`.dc-wizard-step[data-step="${targetStep}"]`);
      if (targetStepEl) {
        targetStepEl.classList.remove('dc-hidden');
        void targetStepEl.offsetWidth;
        targetStepEl.classList.add('dc-wizard-step--active');
      }

      this.state.currentStep = targetStep;
      this.updateWizardUI();

      if (targetStep === 6) {
        this.populateSummary();
      }

      this.scrollToTop();
    }

    updateWizardUI() {
      const progressPercentage = ((this.state.currentStep - 1) / (this.state.totalSteps - 1)) * 100;
      if (this.progressFill) this.progressFill.style.width = `${progressPercentage}%`;

      this.stepIndicators.forEach((indicator) => {
        const stepNum = parseInt(indicator.dataset.stepIndicator, 10);
        indicator.classList.remove('dc-step-indicator--active', 'dc-step-indicator--completed');

        if (stepNum === this.state.currentStep) {
          indicator.classList.add('dc-step-indicator--active');
        } else if (stepNum < this.state.currentStep) {
          indicator.classList.add('dc-step-indicator--completed');
        }
      });

      if (this.state.currentStep === 1) {
        this.btnPrev.classList.add('dc-hidden');
      } else {
        this.btnPrev.classList.remove('dc-hidden');
      }

      if (this.state.currentStep === this.state.totalSteps) {
        this.btnNext.classList.add('dc-hidden');
        this.btnSubmit.classList.remove('dc-hidden');
      } else {
        this.btnNext.classList.remove('dc-hidden');
        this.btnSubmit.classList.add('dc-hidden');
      }
    }

    bindFormValidationEvents() {
      const inputs = this.root.querySelectorAll('.dc-input, .dc-textarea');
      inputs.forEach((input) => {
        input.addEventListener('blur', () => this.validateField(input));
        input.addEventListener('input', () => {
          if (input.classList.contains('dc-input--error') || input.classList.contains('dc-textarea--error')) {
            this.validateField(input);
          }
        });
      });

      if (this.descInput && this.charCountDisplay) {
        this.descInput.addEventListener('input', () => {
          this.charCountDisplay.textContent = this.descInput.value.length;
        });
      }
    }

    validateField(field) {
      const errorSpan = field.closest('.dc-form-group, .dc-policy-checkbox-wrapper')?.querySelector('.dc-error-msg');
      let isValid = true;
      let errorMessage = field.dataset.error || 'Bu alan zorunludur.';

      if (field.hasAttribute('required') && !field.value.trim()) {
        isValid = false;
      } else if (field.type === 'email' && field.value.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(field.value.trim())) {
          isValid = false;
          errorMessage = 'Geçerli bir e-posta adresi giriniz.';
        }
      } else if (field.type === 'tel' && field.value.trim()) {
        const phoneRegex = /^[0-9\s\+\-\(\)]{10,15}$/;
        if (!phoneRegex.test(field.value.trim())) {
          isValid = false;
          errorMessage = 'Geçerli bir telefon numarası giriniz.';
        }
      } else if (field.hasAttribute('minlength') && field.value.trim()) {
        const minLen = parseInt(field.getAttribute('minlength'), 10);
        if (field.value.trim().length < minLen) {
          isValid = false;
          errorMessage = `En az ${minLen} karakter girmelisiniz.`;
        }
      }

      if (!isValid) {
        field.classList.add(field.tagName === 'TEXTAREA' ? 'dc-textarea--error' : 'dc-input--error');
        if (errorSpan) errorSpan.textContent = errorMessage;
      } else {
        field.classList.remove('dc-input--error', 'dc-textarea--error');
        if (errorSpan) errorSpan.textContent = '';
      }

      return isValid;
    }

    validateCurrentStep() {
      const currentStepEl = this.root.querySelector(`.dc-wizard-step[data-step="${this.state.currentStep}"]`);
      if (!currentStepEl) return true;

      let stepIsValid = true;

      const fields = currentStepEl.querySelectorAll('input[required]:not([type="radio"]):not([type="checkbox"]), textarea[required]');
      fields.forEach((field) => {
        if (!this.validateField(field)) stepIsValid = false;
      });

      if (this.state.currentStep === 2) {
        const categorySelected = currentStepEl.querySelector('input[name="product_category"]:checked');
        const colorSelected = currentStepEl.querySelector('input[name="product_color"]:checked');
        const catError = this.root.querySelector('#dc-error-category');
        const colorError = this.root.querySelector('#dc-error-color');

        if (!categorySelected) {
          stepIsValid = false;
          if (catError) catError.textContent = 'Lütfen bir ürün kategorisi seçiniz.';
        } else if (catError) catError.textContent = '';

        if (!colorSelected) {
          stepIsValid = false;
          if (colorError) colorError.textContent = 'Lütfen kaplama rengini seçiniz.';
        } else if (colorError) colorError.textContent = '';
      }

      if (this.state.currentStep === 3) {
        const issueSelected = currentStepEl.querySelector('input[name="issue_type"]:checked');
        const issueError = this.root.querySelector('#dc-error-issue');
        if (!issueSelected) {
          stepIsValid = false;
          if (issueError) issueError.textContent = 'Lütfen bir işlem türü seçiniz.';
        } else if (issueError) issueError.textContent = '';
      }

      if (this.state.currentStep === 5) {
        const policyCheckbox = currentStepEl.querySelector('#dc-policy-accept');
        const policyError = policyCheckbox?.closest('.dc-policy-checkbox-wrapper')?.querySelector('.dc-error-msg');
        if (policyCheckbox && !policyCheckbox.checked) {
          stepIsValid = false;
          if (policyError) policyError.textContent = 'Devam etmek için bakım politikasını onaylamalısınız.';
        } else if (policyError) policyError.textContent = '';
      }

      return stepIsValid;
    }

    bindMediaUploadEvents() {
      if (this.photoDropzone && this.photoInput) {
        this.setupDropzone(this.photoDropzone, this.photoInput, (files) => this.handlePhotoFiles(files));
      }
      if (this.videoDropzone && this.videoInput) {
        this.setupDropzone(this.videoDropzone, this.videoInput, (files) => this.handleVideoFile(files[0]));
      }
    }

    setupDropzone(dropzone, input, callback) {
      ['dragenter', 'dragover'].forEach(ev => dropzone.addEventListener(ev, e => { e.preventDefault(); dropzone.classList.add('dc-dropzone--dragover'); }));
      ['dragleave', 'drop'].forEach(ev => dropzone.addEventListener(ev, e => { e.preventDefault(); dropzone.classList.remove('dc-dropzone--dragover'); }));
      dropzone.addEventListener('drop', e => { if (e.dataTransfer.files.length) callback(Array.from(e.dataTransfer.files)); });
      input.addEventListener('change', () => { if (input.files.length) { callback(Array.from(input.files)); input.value = ''; } });
    }

    handlePhotoFiles(files) {
      files.forEach(file => {
        if (this.state.uploadedPhotos.length >= this.state.maxPhotos) return;
        this.state.uploadedPhotos.push(file);
        this.renderPhotoPreview(file, this.state.uploadedPhotos.length - 1);
      });
    }

    renderPhotoPreview(file, index) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const item = document.createElement('div');
        item.className = 'dc-preview-item';
        item.innerHTML = `<img src="${e.target.result}"><button type="button" class="dc-preview-remove">&times;</button>`;
        item.querySelector('button').addEventListener('click', () => {
          this.state.uploadedPhotos.splice(index, 1);
          this.photoPreviewGrid.innerHTML = '';
          this.state.uploadedPhotos.forEach((f, idx) => this.renderPhotoPreview(f, idx));
        });
        this.photoPreviewGrid.appendChild(item);
      };
      reader.readAsDataURL(file);
    }

    handleVideoFile(file) {
      if (!file) return;
      this.state.uploadedVideo = file;
      this.videoPreviewGrid.innerHTML = `<div class="dc-preview-item"><video src="${URL.createObjectURL(file)}" controls></video></div>`;
    }

    populateSummary() {
      const getVal = sel => this.root.querySelector(sel)?.value || '-';
      const getChecked = name => this.root.querySelector(`input[name="${name}"]:checked`)?.value || '-';

      this.setSummaryText('#dc-sum-order', getVal('#dc-order-number'));
      this.setSummaryText('#dc-sum-fullname', getVal('#dc-fullname'));
      this.setSummaryText('#dc-sum-phone', getVal('#dc-phone'));
      this.setSummaryText('#dc-sum-email', getVal('#dc-email'));
      this.setSummaryText('#dc-sum-category', getChecked('product_category'));
      this.setSummaryText('#dc-sum-color', getChecked('product_color'));
      this.setSummaryText('#dc-sum-issue', getChecked('issue_type'));
      this.setSummaryText('#dc-sum-photos', `${this.state.uploadedPhotos.length} Adet`);
      this.setSummaryText('#dc-sum-video', this.state.uploadedVideo ? 'Var' : 'Yok');
      this.setSummaryText('#dc-sum-desc', getVal('#dc-description'));
    }

    setSummaryText(sel, txt) {
      const el = this.root.querySelector(sel);
      if (el) el.textContent = txt;
    }

    /**
     * Headless Bridge: Transmits wizard data into hidden Shopify Forms engine
     */
    handleHeadlessSubmit() {
      this.toggleSkeletonLoader(true, 'Talebiniz güvenli sunucularımıza işleniyor...');

      try {
        const hiddenForm = document.querySelector('shopify-form form, [data-shopify-forms-container] form');
        if (hiddenForm) {
          const getVal = sel => this.root.querySelector(sel)?.value || '';
          const getChecked = name => this.root.querySelector(`input[name="${name}"]:checked`)?.value || '';

          const nameField = hiddenForm.querySelector('input[name*="first_name"], input[name*="last_name"], input[placeholder*="Ad"]');
          if (nameField) nameField.value = getVal('#dc-fullname');

          const emailField = hiddenForm.querySelector('input[type="email"]');
          if (emailField) emailField.value = getVal('#dc-email');

          const phoneField = hiddenForm.querySelector('input[type="tel"], input[name*="telefon"]');
          if (phoneField) phoneField.value = getVal('#dc-phone');

          const orderField = hiddenForm.querySelector('input[name*="siparis"], input[placeholder*="Sipariş"]');
          if (orderField) orderField.value = getVal('#dc-order-number');

          const selects = hiddenForm.querySelectorAll('select');
          selects.forEach(sel => {
            if (sel.name.includes('kategori')) sel.value = getChecked('product_category');
            if (sel.name.includes('islem') || sel.name.includes('tur')) sel.value = getChecked('issue_type');
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          });

          const fileInput = hiddenForm.querySelector('input[type="file"]');
          if (fileInput && this.state.uploadedPhotos.length > 0) {
            const dt = new DataTransfer();
            this.state.uploadedPhotos.forEach(f => dt.items.add(f));
            fileInput.files = dt.files;
            fileInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          const submitBtn = hiddenForm.querySelector('button[type="submit"], input[type="submit"]');
          if (submitBtn) submitBtn.click();
        }
      } catch (err) {
        console.warn('Headless sync warning:', err);
      }

      setTimeout(() => {
        const randomNum = Math.floor(10000 + Math.random() * 90000);
        this.state.generatedTicket = `DLC-2026-${randomNum}`;

        const ticketEl = this.root.querySelector('#dc-generated-ticket');
        if (ticketEl) ticketEl.textContent = this.state.generatedTicket;

        this.toggleSkeletonLoader(false);
        if (this.wizardForm) this.wizardForm.reset();
        this.state.uploadedPhotos = [];
        this.state.uploadedVideo = null;
        if (this.photoPreviewGrid) this.photoPreviewGrid.innerHTML = '';
        if (this.videoPreviewGrid) this.videoPreviewGrid.innerHTML = '';
        
        this.goToStep(1);
        this.switchView('success');
        this.showToast('Bakım talebiniz başarıyla sisteme kaydedildi.', 'success');
      }, 1500);
    }

    toggleSkeletonLoader(show, text = 'İşleminiz hazırlanıyor...') {
      if (!this.skeletonOverlay) return;
      const tEl = this.skeletonOverlay.querySelector('.dc-skeleton-text');
      if (tEl) tEl.textContent = text;
      show ? this.skeletonOverlay.classList.remove('dc-hidden') : this.skeletonOverlay.classList.add('dc-hidden');
    }

    bindTrackingEvents() {
      if (this.trackingForm) {
        this.trackingForm.addEventListener('submit', (e) => {
          e.preventDefault();
          const tInput = this.root.querySelector('#dc-track-ticket');
          const eInput = this.root.querySelector('#dc-track-email');
          if (!tInput.value.trim() || !eInput.value.trim()) {
            this.showToast('Lütfen talep numarası ve e-posta adresinizi giriniz.', 'error');
            return;
          }
          this.simulateTrackingQuery(tInput.value.trim().toUpperCase());
        });
      }
    }

    simulateTrackingQuery(ticketNo) {
      this.toggleSkeletonLoader(true, 'Sistem kayıtları sorgulanıyor...');
      setTimeout(() => {
        this.toggleSkeletonLoader(false);
        if (this.timelineContainer) {
          this.timelineContainer.classList.remove('dc-hidden');
          const dTicket = this.root.querySelector('#dc-display-ticket');
          if (dTicket) dTicket.textContent = ticketNo;
          this.animateTimelineItems();
        }
        this.showToast('Talep durumu başarıyla getirildi.', 'success');
      }, 1200);
    }

    animateTimelineItems() {
      this.timelineItems.forEach((item, index) => {
        item.style.opacity = '0';
        setTimeout(() => {
          const isFinished = item.classList.contains('dc-timeline-item--completed') || item.classList.contains('dc-timeline-item--active');
          item.style.opacity = isFinished ? '1' : '0.4';
        }, index * 100);
      });
    }

    bindAccordionEvents() {
      this.accordionTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
          const content = trigger.nextElementSibling;
          this.accordionTriggers.forEach(ot => {
            if (ot !== trigger) {
              ot.setAttribute('aria-expanded', 'false');
              if (ot.nextElementSibling) ot.nextElementSibling.style.maxHeight = null;
            }
          });
          if (isExpanded) {
            trigger.setAttribute('aria-expanded', 'false');
            if (content) content.style.maxHeight = null;
          } else {
            trigger.setAttribute('aria-expanded', 'true');
            if (content) content.style.maxHeight = `${content.scrollHeight + 50}px`;
          }
        });
      });
    }

    showToast(message, type = 'success') {
      if (!this.toastContainer) return;
      const toast = document.createElement('div');
      toast.className = `dc-toast dc-toast--${type}`;
      toast.innerHTML = `<span class="dc-toast__text">${message}</span>`;
      this.toastContainer.appendChild(toast);
      setTimeout(() => toast.classList.add('dc-toast--show'), 10);
      setTimeout(() => {
        toast.classList.remove('dc-toast--show');
        setTimeout(() => toast.remove(), 400);
      }, 4000);
    }

    initIntersectionObserver() {
      const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
            obs.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      this.root.querySelectorAll('.dc-card, .dc-policy-card').forEach((el, i) => {
        el.style.opacity = '0';
        el.style.transform = 'translateY(20px)';
        el.style.transition = `all 0.5s ease ${i * 0.08}s`;
        observer.observe(el);
      });
    }

    initSmoothScroll() {
      this.root.querySelectorAll('a[href^="#dc-view-"]').forEach(link => {
        link.addEventListener('click', e => {
          e.preventDefault();
          const target = link.dataset.target;
          if (target) this.switchView(target);
        });
      });
    }
  }

  const initDeliadoreCareSystem = () => {
    document.querySelectorAll('.dc-wrapper').forEach(section => {
      if (!section.dataset.initialized) {
        new DeliadoreCare(section);
        section.dataset.initialized = 'true';
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDeliadoreCareSystem);
  } else {
    initDeliadoreCareSystem();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('.dc-wrapper')) initDeliadoreCareSystem();
  });
})();